// src/pages/ProjectDetails.jsx
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import placeholderImage from "../assets/placeholder.jpg";
import {
  Download,
  Trash2,
  X,
  ChevronDown,
  Check,
  ExternalLink,
  Wand2,
} from "lucide-react";

/** ---------- Canonical feature labels (match Generate.jsx cards) ---------- */
const FEATURE_LABELS = {
  // Ads
  "product-avatar-video": "Product Avatar Video",
  "ad-marketing-video": "Avatar Explainer Video",
  "product-avatar-image": "Product Avatar Image",
  "photoshoot-anyshoot": "Product Photoshoot",
  "background-replacer": "Product Background Changer",
  "ad-creative": "Ad Creative Image",

  // Improvements
  "remove-bg": "Remove Image Background",
  "color-restoration": "Color Restoration",
  enhancer: "Enhancer",
  outpaint: "Extend Image (Outpaint)",
  "light-fix": "Light Fix",
  "object-replacer": "Object Replacer",
  "object-remover": "Object Remover",
  "old-photos-restoration": "Old Photos Restoration",
  upscaler: "Upscaler",
};

/** Route mapping for each canonical feature key (used by "Go to tool" button) */
const FEATURE_ROUTES = {
  // Ads
  "product-avatar-image": "/generate/product-avatar-images",
  "product-avatar-video": "/generate/product-avatar-video",
  "ad-marketing-video": "/generate/ad-marketing-video",
  "ad-creative": "/generate/adcreatives",
  "photoshoot-anyshoot": "/generate/photoshoot",

  // Improvements
  "background-replacer": "/generate/photoshoot",
  "remove-bg": "/generate/remove-bg",
  "object-replacer": "/generate/object-replacer",
  "object-remover": "/generate/object-remover",
  enhancer: "/generate/enhancer",
  outpaint: "/generate/outpaint",
  "light-fix": "/generate/light-fix",
  upscaler: "/generate/upscaler",
  "color-restoration": "/generate/color-restoration",
  "old-photos-restoration": "/generate/old-photos-restoration",
};

/** Exclusions: things we never show (raw/analyzed inputs) */
const EXCLUDE_FEATURES = new Set([
  "analyzed",
  "analysis",
  "scrape",
  "scraped",
  "raw",
  "input",
  "inputs",
  "source",
]);

/**
 * Only treat assets from Generate-page tools as "generated".
 * This ensures generic "generated" buckets are hidden from the UI.
 */
const ALLOWED_FEATURE_KEYS = new Set(Object.keys(FEATURE_LABELS));

/** Normalize various BE/legacy keys → canonical feature keys (fallback to raw key) */
function normalizeFeatureKey(a) {
  const raw = a?.metadata?.feature ?? a?.feature ?? a?.source ?? "";

  const s = String(raw || "").toLowerCase().trim();

  // Canonical mappings
  if (s.includes("product_avatar_video")) return "product-avatar-video";
  if (s.includes("product_avatar_image")) return "product-avatar-image";
  if (s.includes("ad_marketing") || s.includes("marketing_video"))
    return "ad-marketing-video";
  if (s.includes("photoshoot_anyshoot")) return "photoshoot-anyshoot";

  if (
    s.includes("background_replacer") ||
    s.includes("background-generator") ||
    s.includes("photoshoot_images")
  )
    return "background-replacer";

  if (s.includes("adcreative") || s === "adcreative" || s === "ad_creative")
    return "ad-creative";

  if (s.includes("remove") && s.includes("bg")) return "remove-bg";
  if (s.includes("remove-background")) return "remove-bg";

  if (s.includes("color_restor") || s.includes("color-restor"))
    return "color-restoration";
  if (s.includes("enhanc")) return "enhancer";
  if (s.includes("outpaint") || s.includes("extend")) return "outpaint";
  if (s.includes("light_fix") || s.includes("lightfix")) return "light-fix";
  if (s.includes("object_replacer")) return "object-replacer";
  if (s.includes("object_removed") || s.includes("object_remover"))
    return "object-remover";
  if (s.includes("old_photo") || s.includes("old-photos"))
    return "old-photos-restoration";
  if (s.includes("upscal")) return "upscaler";

  // If it's explicitly an excluded bucket, pass it through so we can drop it later
  if (EXCLUDE_FEATURES.has(s)) return s;

  // Fallback to raw key so we don't lose the asset (we'll still filter by ALLOWED_FEATURE_KEYS below)
  return s;
}

function featureToLabel(key) {
  if (!key) return "Generated Asset";
  return FEATURE_LABELS[key] || "Generated Asset";
}

/** Utility: is this a generated asset (vs analyzed inputs)? */
function isGeneratedFeatureKey(key) {
  if (!key) return false;
  const s = String(key).toLowerCase();
  if (EXCLUDE_FEATURES.has(s)) return false;
  // Only include assets from known Generate tools
  return ALLOWED_FEATURE_KEYS.has(s);
}

/* ===== Helpers to map items into unified shape (generated only) ===== */
function toUnifiedItem(a, sourceTag) {
  const featureKey = normalizeFeatureKey(a);
  if (!isGeneratedFeatureKey(featureKey)) return null;

  const url = a.url || a.image_url || a.video_url || "";
  if (!url) return null;

  const isVideo =
    a.type === "video" ||
    a.resource_type === "video" ||
    a.kind === "video" ||
    a?.metadata?.type === "video";

  return {
    id: a.id || `${featureKey}-${url}`,
    type: isVideo ? "video" : "image",
    featureKey,
    url,
    name: featureToLabel(featureKey),
    created_at: a.created_at || a.createdAt || null,
    __sourceGroup: sourceTag,
  };
}

/** Flatten assetsByFeature into rows like the flat assets array */
function flattenAssetsByFeature(abf) {
  const out = [];
  if (!abf || typeof abf !== "object") return out;
  for (const [featureKey, rows] of Object.entries(abf)) {
    if (!Array.isArray(rows)) continue;
    for (const r of rows) {
      // Attach feature if missing so normalizer has a chance
      if (!r?.metadata?.feature) {
        r.metadata = { ...(r.metadata || {}), feature: featureKey };
      }
      out.push(r);
    }
  }
  return out;
}

/* ===== Small UI helpers ===== */
function formatWhen(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/** Modal controls */
function useModalControls(onClose, enabled) {
  const backdropRef = useRef(null);

  const onKeyDown = useCallback(
    (e) => {
      if (!enabled) return;
      if (e.key === "Escape") onClose();
    },
    [onClose, enabled]
  );

  const onBackdropMouseDown = useCallback(
    (e) => {
      if (!enabled) return;
      if (e.target === backdropRef.current) onClose();
    },
    [onClose, enabled]
  );

  useEffect(() => {
    if (!enabled) return undefined;
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onKeyDown, enabled]);

  return { backdropRef, onBackdropMouseDown };
}

/* ===== Brand-styled SingleSelectDropdown ===== */
function SingleSelectDropdown({ options, value, onChange, label }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target))
        setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const currentLabel = useMemo(() => {
    if (!value) return "All";
    const found = options.find((o) => o.value === value);
    return found?.label || "All";
  }, [value, options]);

  return (
    <div className="relative" ref={rootRef} style={{ width: 224 }}>
      <label className="block text-xs text-white/70 mb-1 ml-1">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={[
          "group relative flex items-center justify-between gap-2",
          "rounded-2xl px-3 h-10",
          "bg-[#17172a] text-white text-sm",
          "border border-[#343454]",
          "shadow-[inset_0_0_0_1px_rgba(255,255,255,0.02)]",
          "transition-all focus:outline-none",
          open
            ? "ring-2 ring-pink-500/40 border-pink-500/50"
            : "hover:border-pink-500/30",
        ].join(" ")}
        style={{ width: 224 }}
      >
        <span className="truncate">{currentLabel}</span>
        <ChevronDown
          size={18}
          className={`shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
        <span className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/5" />
      </button>

      {open && (
        <ul
          role="listbox"
          tabIndex={-1}
          className={[
            "absolute left-0 top-full mt-2",
            "z-50 rounded-2xl",
            "bg-[#151528]/95 backdrop-blur-sm",
            "border border-[#343454]",
            "shadow-2xl shadow-black/40",
            "max-h-72 overflow-auto",
            "animate-[fadeIn_120ms_ease-out]",
          ].join(" ")}
          style={{ width: 224 }}
        >
          <li
            role="option"
            aria-selected={!value}
            onClick={() => onChange(null)}
            className={[
              "cursor-pointer px-3 py-2 text-sm select-none",
              "flex items-center justify-between",
              !value ? "bg-pink-600/15 text-pink-300" : "hover:bg-[#232340]",
            ].join(" ")}
          >
            <span>All</span>
            {!value && <Check size={18} className="text-pink-400" />}
          </li>

          {options.length === 0 && (
            <li className="px-3 py-2 text-sm text-gray-400">No options</li>
          )}

          {options.map(({ value: optValue, label: optLabel }) => {
            const isSelected = value === optValue;
            return (
              <li
                key={optValue}
                role="option"
                aria-selected={isSelected}
                onClick={() => onChange(optValue)}
                className={[
                  "cursor-pointer px-3 py-2 text-sm select-none",
                  "flex items-center justify-between",
                  isSelected
                    ? "bg-pink-600/15 text-pink-300"
                    : "hover:bg-[#232340]",
                ].join(" ")}
              >
                <span className="truncate">{optLabel}</span>
                {isSelected && <Check size={18} className="text-pink-400" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Toast
function Toast({ message, onClose }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [message, onClose]);
  if (!message) return null;
  return (
    <div className="fixed top-6 right-8 z-[9999] bg-pink-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fadeIn border border-pink-400">
      <Trash2 size={20} />
      <span className="text-sm">{message}</span>
      <button
        onClick={onClose}
        className="ml-2 text-white hover:text-black transition"
        aria-label="Close toast"
      >
        <X size={16} />
      </button>
    </div>
  );
}

/* =========================== Page =========================== */

const PAGE_SIZE = 24; // infinite scroll batch size

export default function ProjectDetails() {
  const { id } = useParams();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [allAssets, setAllAssets] = useState([]); // full generated-only list
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [viewingAsset, setViewingAsset] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState("");

  // Single selected source — null = All
  const [selectedSource, setSelectedSource] = useState(null);

  // Infinite scroll sentinel
  const sentinelRef = useRef(null);

  // ----- Map helpers that DO NOT drop unknown-but-generated features BEFORE filter -----
  const mapDbLike = useCallback((rows, tag) => {
    if (!Array.isArray(rows)) return [];
    return rows.map((a) => toUnifiedItem(a, tag)).filter(Boolean);
  }, []);

  // ===== Data fetching
  const fetchProject = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(res.data);
      setError(null);

      // 1) Flat assets
      const flatAssets = mapDbLike(res.data?.assets, "assets_table");

      // 2) assetsByFeature (flattened)
      const fromByFeature = mapDbLike(
        flattenAssetsByFeature(res.data?.assetsByFeature),
        "assets_by_feature"
      );

      // 3) Cloudflare (if any)
      const cfAssets = mapDbLike(
        res.data?.cloudflareAssets || res.data?.cloudflare_assets,
        "cloudflare_assets"
      );

      // Merge & de-dup
      const combined = [...flatAssets, ...fromByFeature, ...cfAssets];
      const byKey = new Map();
      combined.forEach((item) => {
        const key = `${item.featureKey}-${item.id || item.url}`;
        byKey.set(key, item);
      });

      setAllAssets(
        Array.from(byKey.values()).sort((a, b) => {
          const ta = new Date(a.created_at || 0).getTime();
          const tb = new Date(b.created_at || 0).getTime();
          return tb - ta;
        })
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch project:", err);
      setError("Failed to load project.");
      setToast("Failed to load project.");
    }
  }, [getToken, id, mapDbLike]);

  const fetchAdCreatives = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/adcreatives/items`,
        {
          params: { projectId: id, offset: 0, limit: 200 },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const creatives = (res.data?.creatives || [])
        .filter((c) => c?.status === "finished" && c?.url)
        .map((c) => ({
          id: c.id,
          type: "image",
          featureKey: "ad-creative",
          url: c.url,
          name: featureToLabel("ad-creative"),
          created_at: c.createdAt || null,
          __sourceGroup: "ad_creatives",
        }));

      setAllAssets((prev) => {
        const keep = prev.filter((a) => a.__sourceGroup !== "ad_creatives");
        const byKey = new Map();
        [...keep, ...creatives].forEach((item) => {
          const key = `${item.featureKey}-${item.id || item.url}`;
          byKey.set(key, item);
        });
        return Array.from(byKey.values()).sort((a, b) => {
          const ta = new Date(a.created_at || 0).getTime();
          const tb = new Date(b.created_at || 0).getTime();
          return tb - ta;
        });
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch ad creatives:", err);
    }
  }, [getToken, id]);

  useEffect(() => {
    fetchProject();
    fetchAdCreatives();
  }, [fetchProject, fetchAdCreatives]);

  // ===== Filtering (single select) and reset pagination
  useEffect(() => {
    const list = selectedSource
      ? allAssets.filter((a) => a.featureKey === selectedSource)
      : allAssets;
    setFilteredAssets(list);
    setVisibleCount(PAGE_SIZE);
  }, [allAssets, selectedSource]);

  // ===== Infinite scrolling
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting) {
          setVisibleCount((c) => Math.min(c + PAGE_SIZE, filteredAssets.length));
        }
      },
      { root: null, rootMargin: "400px 0px 0px 0px", threshold: 0 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [filteredAssets.length]);

  // ===== Actions (delete/download) — unchanged logic =====
  const handleDelete = useCallback(
    async (asset) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm("Delete this asset?")) return;
      try {
        const token = await getToken();
        switch (asset.featureKey) {
          case "background-replacer":
            await axios.delete(
              `${process.env.REACT_APP_BACKEND_URL}/api/photoshoot/delete`,
              {
                data: { imageUrl: asset.url, projectId: id },
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            break;
          case "photoshoot-anyshoot":
            await axios.delete(
              `${process.env.REACT_APP_BACKEND_URL}/api/product-anyshoot/delete`,
              {
                data: { projectId: id, taskId: asset.id },
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            break;
          case "product-avatar-image":
            await axios.delete(
              `${process.env.REACT_APP_BACKEND_URL}/api/product-avatar-images/delete`,
              {
                data: { projectId: id, taskId: asset.id },
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            break;
          case "product-avatar-video":
            await axios.delete(
              `${process.env.REACT_APP_BACKEND_URL}/api/product-avatar-video/delete`,
              {
                data: { projectId: id, taskId: asset.taskId || asset.id },
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            break;
          case "ad-marketing-video":
            await axios.delete(
              `${process.env.REACT_APP_BACKEND_URL}/api/videos/marketing/delete`,
              {
                data: { projectId: id, taskId: asset.taskId || asset.id },
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            break;
          case "remove-bg":
            await axios.delete(
              `${process.env.REACT_APP_BACKEND_URL}/api/remove-background/remove-bg`,
              {
                data: { imageUrl: asset.url, projectId: id },
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            break;
          case "ad-creative":
            await axios.delete(
              `${process.env.REACT_APP_BACKEND_URL}/api/adcreatives/delete`,
              {
                data: { projectId: id, id: asset.id },
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            break;
          case "upscaler":
            await axios.delete(
              `${process.env.REACT_APP_BACKEND_URL}/api/upscaler/delete`,
              {
                data: { imageUrl: asset.url, projectId: id },
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            break;
          case "enhancer":
            await axios.delete(
              `${process.env.REACT_APP_BACKEND_URL}/api/enhancer/delete`,
              {
                data: { imageUrl: asset.url, projectId: id },
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            break;
          case "outpaint":
            await axios.delete(
              `${process.env.REACT_APP_BACKEND_URL}/api/outpaint/delete`,
              {
                data: { imageUrl: asset.url, projectId: id },
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            break;
          case "light-fix":
            await axios.delete(
              `${process.env.REACT_APP_BACKEND_URL}/api/lightfix/delete`,
              {
                data: { imageUrl: asset.url, projectId: id },
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            break;
          case "object-replacer":
            await axios.delete(
              `${process.env.REACT_APP_BACKEND_URL}/api/object-replacer/delete`,
              {
                data: { imageUrl: asset.url, projectId: id },
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            break;
          case "color-restoration":
            await axios.delete(
              `${process.env.REACT_APP_BACKEND_URL}/api/color-restoration/restore`,
              {
                data: { imageUrl: asset.url, projectId: id },
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            break;
          case "object-remover":
            await axios.delete(
              `${process.env.REACT_APP_BACKEND_URL}/api/object-remover/remove`,
              {
                data: { imageUrl: asset.url, projectId: id },
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            break;
          case "old-photos-restoration":
            await axios.delete(
              `${process.env.REACT_APP_BACKEND_URL}/api/old-photo-restore/delete`,
              {
                data: { imageUrl: asset.url, projectId: id },
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            break;

          default:
            // eslint-disable-next-line no-alert
            alert("Unknown asset type; cannot delete.");
            return;
        }

        setAllAssets((prev) => prev.filter((a) => a.id !== asset.id));
        if (viewingAsset && viewingAsset.id === asset.id) {
          setViewingAsset(null);
        }
        setToast("Asset deleted");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Delete failed:", err);
        // eslint-disable-next-line no-alert
        alert("Delete failed");
      }
    },
    [getToken, id, viewingAsset]
  );

  const handleDownload = useCallback((url) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = "";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, []);

  /* ===================== Preview Modal ===================== */
  function AssetPreview() {
    const onClose = () => setViewingAsset(null);
    const enabled = Boolean(viewingAsset);
    const { backdropRef, onBackdropMouseDown } = useModalControls(
      onClose,
      enabled
    );

    if (!viewingAsset) return null;

    const url = viewingAsset.url;
    const isVideo = viewingAsset.type === "video";
    const goToPath = FEATURE_ROUTES[viewingAsset.featureKey];

    return (
      <div
        ref={backdropRef}
        onMouseDown={onBackdropMouseDown}
        className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4 select-none"
      >
        <div className="relative bg-[#22223a] rounded-2xl max-w-[98vw] shadow-2xl p-0 flex flex-col">
          {/* Top-right close */}
          <button
            onClick={onClose}
            className="absolute top-2 right-2 bg-white/90 hover:bg-pink-600 text-black hover:text-white rounded-full p-2 shadow transition"
            title="Close preview"
            aria-label="Close preview"
          >
            <X size={21} />
          </button>

          <div className="p-6 flex flex-col items-center justify-center">
            {isVideo ? (
              <video
                src={url}
                controls
                autoPlay
                className="max-w-[95vw] max-h-[78vh] object-contain rounded-xl bg-black"
                style={{ boxShadow: "0 4px 32px 0 rgba(30,25,60,0.5)" }}
              />
            ) : (
              <img
                src={url}
                alt={viewingAsset.name || "Preview"}
                className="max-w-[95vw] max-h-[78vh] object-contain rounded-xl bg-black"
                style={{ boxShadow: "0 4px 32px 0 rgba(30,25,60,0.5)" }}
                draggable={false}
                onError={(e) => {
                  e.currentTarget.src = placeholderImage;
                }}
              />
            )}
          </div>

          {/* Bottom actions — order: Download, Open, Go to tool, Delete */}
          <div className="flex justify-center gap-4 pb-5">
            <button
              onClick={() => handleDownload(url)}
              className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
            >
              Download
            </button>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
            >
              Open in new tab
            </a>
            {goToPath && (
              <button
                onClick={() => navigate(goToPath)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6 py-2 font-semibold shadow transition flex items-center gap-2"
                title="Open the generator used for this asset"
              >
                <Wand2 size={18} />
                Go to tool
              </button>
            )}
            <button
              onClick={() => handleDelete(viewingAsset)}
              className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6 py-2 font-semibold shadow transition"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== Source lists for dropdown (use whatever features exist) =====
  const allSources = useMemo(() => {
    const unique = new Set();
    allAssets.forEach((a) => a.featureKey && unique.add(a.featureKey));
    return Array.from(unique).sort();
  }, [allAssets]);

  const dropdownOptions = useMemo(
    () => allSources.map((v) => ({ value: v, label: featureToLabel(v) })),
    [allSources]
  );

  // NEW: track total vs displayed counts for header text
  const totalAssetsCount = filteredAssets.length;
  const displayedCount = Math.min(visibleCount, totalAssetsCount);
  const visibleItems = filteredAssets.slice(0, visibleCount);

  // NEW: Empty state CTA
  const handleGoGenerate = useCallback(() => {
    navigate("/generate");
  }, [navigate]);

  return (
    <div className="w-full px-2 sm:px-4 md:px-8 py-5 sm:py-8">
      <Toast message={toast} onClose={() => setToast("")} />

      <div className="max-w-[1600px] mx-auto bg-[#15152B] border border-[#2A2A40] rounded-3xl shadow-lg overflow-hidden">
        {/* Sticky header */}
        <div className="sticky top-0 z-30 bg-[#15152B]/90 backdrop-blur border-b border-[#2A2A40]">
          <div className="px-3 sm:px-6 md:px-8 py-4 sm:py-5">
            {/* Title and counts */}
            <div className="min-w-0">
              <h1
                className="text-2xl sm:text-3xl font-bold text-white"
                title={project?.product_name || "Untitled"}
              >
                <span className="block truncate">
                  Assets for: {project?.product_name || "Untitled"}
                </span>
              </h1>
              <p className="text-xs text-white/60 mt-1">
                Showing{" "}
                <span className="text-white font-semibold">{displayedCount}</span>{" "}
                out of{" "}
                <span className="text-white font-semibold">
                  {totalAssetsCount}
                </span>{" "}
                items
              </p>
            </div>

            {/* Filter dropdown directly UNDERNEATH the title */}
            <div className="mt-4">
              <SingleSelectDropdown
                options={dropdownOptions}
                value={selectedSource}
                onChange={setSelectedSource}
                label="Filter by Feature"
              />
            </div>
          </div>
        </div>

        {/* Assets grid */}
        <div className="px-3 sm:px-6 md:px-8 py-6 sm:py-8">
          {filteredAssets.length === 0 ? (
            <div className="rounded-2xl border border-[#2A2A40] bg-[#1b1b32] p-10 text-center text-white/70">
              <div className="text-lg font-semibold mb-2">
                No generated assets yet
              </div>
              <div className="text-sm max-w-[520px] mx-auto">
                You don’t have any generated assets for this project yet. Generate
                one now and it will appear here instantly.
              </div>

              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={handleGoGenerate}
                  className={[
                    "inline-flex items-center justify-center gap-2",
                    "bg-pink-600 hover:bg-pink-700 text-white",
                    "rounded-full px-7 py-3 font-semibold shadow-lg",
                    "transition transform hover:scale-[1.02]",
                    "ring-1 ring-pink-400/30",
                  ].join(" ")}
                >
                  <Wand2 size={18} />
                  Generate one now
                </button>
              </div>
            </div>
          ) : (
            <>
              <div
                className="
                  grid gap-4 sm:gap-6
                  grid-cols-1
                  xs:grid-cols-2
                  md:grid-cols-3
                  lg:grid-cols-4
                  [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]
                "
              >
                {visibleItems.map((asset) => {
                  const key = asset.id || asset.url;
                  const srcUrl = asset.url;
                  const isVideo = asset.type === "video";
                  const goToPath = FEATURE_ROUTES[asset.featureKey];

                  return (
                    <div
                      key={key}
                      className="relative group bg-gradient-to-br from-[#1C1C2E] to-[#252538] border border-[#2A2A40] text-white rounded-2xl p-2 sm:p-3 transition transform hover:scale-[1.03] hover:shadow-xl hover:ring-1 hover:ring-pink-500/30 min-h-[160px] h-[210px] sm:h-[300px] flex items-center justify-center"
                      style={{ overflow: "hidden" }}
                      onClick={() => setViewingAsset(asset)}
                    >
                      {isVideo ? (
                        // ✅ No controls + no pointer events: videos can only be played in the preview modal
                        <video
                          src={srcUrl}
                          muted
                          playsInline
                          preload="metadata"
                          className="rounded-lg w-full object-contain bg-black/50 pointer-events-none"
                          style={{
                            height: "100%",
                            maxHeight: "calc(100% - 40px)",
                          }}
                        />
                      ) : (
                        <img
                          src={srcUrl}
                          alt={asset.name || "Asset"}
                          className="object-contain w-full mx-auto rounded-lg transition-opacity duration-300 group-hover:opacity-80"
                          onError={(e) => {
                            e.currentTarget.src = placeholderImage;
                          }}
                          draggable={false}
                          style={{
                            background: "#111124",
                            height: "100%",
                            maxHeight: "calc(100% - 40px)",
                          }}
                        />
                      )}

                      {/* Bottom meta */}
                      <div className="absolute left-0 right-0 bottom-2 px-3 flex items-center justify-between pointer-events-none">
                        <div
                          className="max-w-[70%] text-[13px] sm:text-[14px] font-bold tracking-tight text-white truncate"
                          title={asset.name}
                          style={{
                            textShadow: "0 0 10px rgba(252,39,131,0.25)",
                          }}
                        >
                          {asset.name}
                        </div>
                        <div
                          className="text-[11px] text-white/80 ml-3 shrink-0 bg-white/10 px-2 py-0.5 rounded-full backdrop-blur-sm"
                          title={
                            asset.created_at
                              ? new Date(asset.created_at).toLocaleString()
                              : ""
                          }
                        >
                          {formatWhen(asset.created_at)}
                        </div>
                      </div>

                      {/* Hover overlay actions */}
                      {srcUrl && (
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none z-20"
                          style={{ background: "rgba(21, 21, 43, 0.15)" }}
                        >
                          <div className="flex gap-3 pointer-events-auto">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(srcUrl);
                              }}
                              className="bg-black/80 hover:bg-pink-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Download"
                            >
                              <Download size={20} />
                            </button>
                            <a
                              href={srcUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="bg-black/80 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Open in new tab"
                            >
                              <ExternalLink size={20} />
                            </a>
                            {goToPath && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(goToPath);
                                }}
                                className="bg-black/80 hover:bg-emerald-600 text-white rounded-full p-3 shadow-lg transition"
                                title="Open the generator used for this asset"
                                aria-label="Open generator"
                              >
                                <Wand2 size={20} />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(asset);
                              }}
                              className="bg-black/80 hover:bg-red-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Delete"
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Infinite scroll sentinel */}
              {visibleItems.length < filteredAssets.length && (
                <div
                  ref={sentinelRef}
                  className="h-10 w-full mt-6 flex items-center justify-center text-white/40"
                >
                  Loading more…
                </div>
              )}
            </>
          )}

          <AssetPreview />
          {error && <div className="text-red-500 mt-6">{error}</div>}
        </div>
      </div>
    </div>
  );
}
